import { customerGradesPath, customerListPath } from "@dashboard/customers/urls";
import { isUrlAbsolute } from "@dashboard/extensions/isUrlAbsolute";
import { type Extension } from "@dashboard/extensions/types";
import { ExtensionsUrls } from "@dashboard/extensions/urls";
import { orderDraftListUrl, orderDraftPath, orderListUrl, orderPath } from "@dashboard/orders/urls";
import {
  productAddPath,
  productBasicInfoPath,
  productListPath,
  productPricePath,
} from "@dashboard/products/urls";
import { productSalesPath, salesStatsPath } from "@dashboard/salesStats/urls";
import { matchPath } from "react-router";

import { type SidebarMenuItem } from "./types";

const ORDER_RESERVED_PATH_SEGMENTS = ["drafts", "settings"];

const getOrderDetailId = (activeUrl: string): string | null => {
  const match = matchPath<{ orderId: string }>(activeUrl, {
    path: orderPath(":orderId"),
    exact: true,
  });

  if (!match?.params.orderId) {
    return null;
  }

  const orderId = decodeURIComponent(match.params.orderId);

  if (ORDER_RESERVED_PATH_SEGMENTS.includes(orderId)) {
    return null;
  }

  return orderId;
};

const getOrderDraftDetailId = (activeUrl: string): string | null => {
  const match = matchPath<{ id: string }>(activeUrl, {
    path: orderDraftPath(":id"),
    exact: true,
  });

  if (!match?.params.id) {
    return null;
  }

  return decodeURIComponent(match.params.id);
};

const resolveExtensionMenuUrl = (extension: Extension): string | undefined => {
  if (isUrlAbsolute(extension.url) || !extension.app.appUrl) {
    return undefined;
  }

  return ExtensionsUrls.resolveDashboardUrlFromAppCompleteUrl(
    extension.url,
    extension.app.appUrl,
    extension.app.id,
  );
};

export const mapToExtensionsItems = (extensions: Extension[], header: SidebarMenuItem) => {
  const items: SidebarMenuItem[] = extensions.map(extension => ({
    id: `extension-${extension.id}`,
    label: extension.label,
    url: resolveExtensionMenuUrl(extension),
    permissions: extension.permissions,
    onClick: extension.open,
    type: "item",
  }));

  if (items.length) {
    items.unshift(header);
  }

  return items;
};

export function isMenuActive(location: string, menuItem: SidebarMenuItem) {
  const menuUrlsToCheck = [...(menuItem.matchUrls || []), menuItem.url]
    .filter((item): item is string => Boolean(item))
    .map(item => item.split("?")[0]);

  if (menuUrlsToCheck.length === 0) {
    return false;
  }

  const activeUrl = getPureUrl(location.split("?")[0]);

  if (isMenuItemExtension(menuItem)) {
    return false;
  }

  const orderDraftListPath = orderDraftListUrl().split("?")[0];
  const orderListPath = orderListUrl().split("?")[0];
  const isDraftOrderDetailPage = getOrderDraftDetailId(activeUrl) !== null;
  const isOrderDetailPage = getOrderDetailId(activeUrl) !== null;

  if (
    menuUrlsToCheck.some(url => url === orderListPath) &&
    (activeUrl === orderDraftListPath || isDraftOrderDetailPage)
  ) {
    return false;
  }

  if (
    menuUrlsToCheck.some(url => url === orderDraftListPath) &&
    (activeUrl === orderDraftListPath || isDraftOrderDetailPage)
  ) {
    return true;
  }

  if (
    menuUrlsToCheck.some(url => url === orderDraftListPath) &&
    isOrderDetailPage &&
    !isDraftOrderDetailPage
  ) {
    return false;
  }

  // 상품별 매출이 매출 통계의 하위 경로라 아래 접두어 매칭에는 둘이 함께 걸린다.
  // 상품별 매출을 보는 동안에는 매출 통계를 활성에서 뺀다 (2026-09-05 KST 추가)
  if (
    menuUrlsToCheck.some(url => url === salesStatsPath) &&
    !!matchPath(activeUrl, { path: productSalesPath })
  ) {
    return false;
  }

  // 회원등급(/customers/grades)도 회원 목록(/customers/)의 하위 경로라 함께 걸린다.
  // 회원등급을 보는 동안에는 '회원'을 활성에서 뺀다 (2026-10-08 KST 추가)
  if (
    menuUrlsToCheck.some(url => url === customerListPath) &&
    !!matchPath(activeUrl, { path: customerGradesPath })
  ) {
    return false;
  }

  // 상품등록(/products/add)도 상품 목록(/products/)의 하위 경로라 함께 걸린다.
  // 등록 화면에서는 '상품'을 활성에서 뺀다 (2026-10-15 KST 추가)
  // 기본정보 관리(/products/basic-info)도 같은 이유로 뺀다 (2026-10-27 KST 추가)
  if (
    menuUrlsToCheck.some(url => url === productListPath) &&
    // 가격 관리도 상품 메뉴와 따로 켜진다 (2026-10-27 KST 수정)
    [productAddPath, productBasicInfoPath, productPricePath].includes(activeUrl)
  ) {
    return false;
  }

  return menuUrlsToCheck.some(menuItemUrl => {
    return !!matchPath(activeUrl, {
      exact: menuItemUrl === "/",
      path: menuItemUrl,
    });
  });
}

const getPureUrl = (url: string) => {
  if (url.includes("/dashboard")) {
    return url.split("/dashboard")[1];
  }

  return url;
};
const isMenuItemExtension = (menuItem: SidebarMenuItem) => menuItem.id.startsWith("extension-");

export const getMenuItemExtension = (
  extensions: Record<
    | "NAVIGATION_CATALOG"
    | "NAVIGATION_ORDERS"
    | "NAVIGATION_CUSTOMERS"
    | "NAVIGATION_DISCOUNTS"
    | "NAVIGATION_TRANSLATIONS"
    | "NAVIGATION_PAGES",
    Extension[]
  >,
  id: string,
) => {
  const extensionsList = Object.values(extensions).reduce(
    (list, extensions) => list.concat(extensions),
    [],
  );
  const extension = extensionsList.find(extension => id === `extension-${extension.id}`);

  return extension;
};
